import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import api, { fmtDateTime } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { TaskDetailDialog } from "@/components/TaskDetailDialog";

const iconFor = (module) => ({
  Leave: Icons.CalendarDays, HRIS: Icons.UserCog, Procurement: Icons.ShoppingCart,
  Finance: Icons.Wallet, Document: Icons.FileText,
}[module] || Icons.CheckSquare);

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [filter, setFilter] = useState("Pending");
  const [open, setOpen] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    const q = filter === "All" ? "" : `?status=${filter}`;
    api.get(`/tasks${q}`).then(({ data }) => setTasks(data));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const act = async (t, action) => {
    if (t.link_type === "payroll_memo" && action !== "open") { setOpen(t); return; }
    if (t.link_type === "leave_request") {
      try {
        await api.post(`/leave/requests/${t.link_id}/${action}`, { reason: "" });
        toast.success(`Task ${action === "approve" ? "approved" : "rejected"}`);
        setOpen(null); load();
      } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    } else if (t.link_type === "purchase_request") {
      try {
        await api.post(`/procurement/requests/${t.link_id}/${action}`, { reason: "" });
        toast.success(`Task ${action === "approve" ? "approved" : "rejected"}`);
        setOpen(null); load();
      } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    } else {
      navigate(t.link_path || "/dashboard");
    }
  };

  return (
    <div className="uemp-fade-in">
      <PageHeader title="My Tasks" subtitle="Approvals and action items assigned to you" />
      <div className="flex gap-2 mb-4">
        {["Pending", "Completed", "All"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`task-filter-${f}`}
            className={`px-3.5 py-1.5 rounded-lg text-sm transition-colors ${filter === f ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
            {f}
          </button>
        ))}
      </div>

      <div className="space-y-3" data-testid="tasks-list">
        {tasks.length === 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-400">
            <Icons.CheckCircle2 className="h-10 w-10 mx-auto mb-3 text-slate-300" />
            No {filter.toLowerCase()} tasks.
          </div>
        )}
        {tasks.map((t) => {
          const Icon = iconFor(t.module);
          const pending = t.status === "Pending";
          return (
            <div key={t.id} role="button" tabIndex={0} onClick={() => setOpen(t)} onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); setOpen(t); } }}
              className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap sm:flex-nowrap items-center gap-4 hover:shadow-sm hover:border-slate-300 cursor-pointer transition-shadow" data-testid={`task-${t.reference}`}>
              <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${pending ? "bg-amber-50 text-amber-600" : "bg-slate-100 text-slate-500"}`}>
                <Icon className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap sm:flex-nowrap items-center gap-x-2 gap-y-1">
                  <span className="text-sm font-medium text-slate-800 sm:truncate">{t.title}</span>
                  <Badge variant="secondary" className="text-[10px] shrink-0">{t.type}</Badge>
                </div>
                <div className="text-xs text-slate-500 truncate">{t.description} · {t.reference}</div>
                <div className="text-[11px] text-slate-400 mt-0.5">{fmtDateTime(t.created_at)} · <span className="text-sky-600">Click to view details</span></div>
              </div>
              {pending ? (
                <div className="flex gap-1.5 shrink-0 w-full sm:w-auto justify-end" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                  <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-xs" onClick={() => act(t, "approve")} data-testid={`task-approve-${t.reference}`}>
                    <Icons.Check className="h-3.5 w-3.5 mr-1" /> Approve
                  </Button>
                  <Button size="sm" variant="outline" className="h-8 text-xs text-red-600" onClick={() => act(t, "reject")} data-testid={`task-reject-${t.reference}`}>Reject</Button>
                </div>
              ) : (
                <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100 shrink-0">{t.resolution || "Completed"}</Badge>
              )}
            </div>
          );
        })}
      </div>
      {open && <TaskDetailDialog task={open} onClose={() => setOpen(null)} onAct={act} onChanged={load} />}
    </div>
  );
}
